import React from 'react';
import styled from '@emotion/styled';
import { CompleteContainer, CompleteTitle, CompleteDesc, CompleteButton } from './SharedStyles';
import { COLORS, FONT_SIZES, FONT_WEIGHTS, SPACING } from './constants';

// Shared constants to eliminate text duplication
const COMPLETION_CONTENT = {
  title: 'Thanks for your review! 💜',
  description1: 'Your thoughts will help others find the perfect product.',
  description2: 'We truly appreciate you taking the time!',
  subDescription: 'Your review will be posted on product page and your profile',
  buttonText: 'Continue Shopping',
} as const;

// Shared base styles to reduce duplication
const BaseCompleteTitle = styled(CompleteTitle)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  font-size: ${FONT_SIZES['2xl']};
  color: ${COLORS.neutral100};
`;

const BaseCompleteDesc = styled(CompleteDesc)`
  color: ${COLORS.neutral80};
  font-weight: ${FONT_WEIGHTS.normal};
`;

const BaseCompleteButton = styled(CompleteButton)`
  width: 100%;
  height: 48px;
  font-size: ${FONT_SIZES.sm};
  font-weight: ${FONT_WEIGHTS.normal};
`;

// PC-specific styles
const PcCompleteContainer = styled(CompleteContainer)``;

const PcCompleteTitle = styled(BaseCompleteTitle)`
  font-weight: ${FONT_WEIGHTS.semibold};
`;

const PcCompleteDesc = styled(BaseCompleteDesc)`
  font-size: ${FONT_SIZES.base};
`;

const PcSubDesc = styled(BaseCompleteDesc)`
  font-size: ${FONT_SIZES.xs};
  color: ${COLORS.neutral50};
`;

const PcCompleteButton = styled(BaseCompleteButton)`
  color: ${COLORS.white};
`;

// Mobile-specific styles
const MobileCompleteContainer = styled(CompleteContainer)`
  align-items: flex-start;
  justify-content: center;
  text-align: left;
  gap: ${SPACING.lg};
  padding-bottom: 80px;
`;

const MobileCompleteTitle = styled(BaseCompleteTitle)`
  font-weight: ${FONT_WEIGHTS.medium};
`;

const MobileCompleteDesc = styled(BaseCompleteDesc)`
  font-size: ${FONT_SIZES.sm};
`;

const MobileSubDesc = styled(BaseCompleteDesc)`
  font-size: ${FONT_SIZES.xs};
  color: ${COLORS.neutral50};
`;

const MobileCompleteButtonWrap = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${COLORS.background};
  padding: ${SPACING.lg} ${SPACING.lg} 68px ${SPACING.lg};
  box-sizing: border-box;
`;

const MobileCompleteButton = styled(BaseCompleteButton)`
  color: ${COLORS.white} !important;
`;

// Shared content component to eliminate JSX duplication
interface CompletionContentProps {
  TitleComponent: React.ComponentType<{ children: React.ReactNode }>;
  DescComponent: React.ComponentType<{ children: React.ReactNode }>;
  SubDescComponent: React.ComponentType<{ children: React.ReactNode }>;
}

const CompletionContent = ({ TitleComponent, DescComponent, SubDescComponent }: CompletionContentProps) => (
  <>
    <TitleComponent>{COMPLETION_CONTENT.title}</TitleComponent>
    <DescComponent>{COMPLETION_CONTENT.description1}</DescComponent>
    <DescComponent>{COMPLETION_CONTENT.description2}</DescComponent>
    <SubDescComponent>{COMPLETION_CONTENT.subDescription}</SubDescComponent>
  </>
);

interface CompleteViewProps {
  onContinue: () => void;
  variant: 'pc' | 'mobile';
}

export const CompleteView = ({ onContinue, variant }: CompleteViewProps) => {
  const handleContinue = () => {
    onContinue();
    window.location.href = '/shop/';
  };

  if (variant === 'mobile') {
    return (
      <>
        <MobileCompleteContainer>
          <CompletionContent
            TitleComponent={MobileCompleteTitle}
            DescComponent={MobileCompleteDesc}
            SubDescComponent={MobileSubDesc}
          />
        </MobileCompleteContainer>
        <MobileCompleteButtonWrap>
          <MobileCompleteButton onClick={handleContinue}>{COMPLETION_CONTENT.buttonText}</MobileCompleteButton>
        </MobileCompleteButtonWrap>
      </>
    );
  }

  return (
    <PcCompleteContainer>
      <CompletionContent TitleComponent={PcCompleteTitle} DescComponent={PcCompleteDesc} SubDescComponent={PcSubDesc} />
      <PcCompleteButton onClick={handleContinue}>{COMPLETION_CONTENT.buttonText}</PcCompleteButton>
    </PcCompleteContainer>
  );
};
